import { useState } from "react";
import { toast } from "sonner";
import { Mail, Upload, Loader2, CheckCircle2, XCircle, AlertTriangle, Trash2, Warehouse, ShieldAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { DateTime24 } from "./DateTime24";
import { api } from "../lib/api";

// Manual "Import Job from Email" dialog.
//
// Feb 2026 refit: this is the ONLY entry point into the intake pipeline
// that a human ever touches. Auto-ingested emails from allowlisted
// senders skip this dialog and land straight in the jobs queue. The
// review-inbox page + custom-fields bag are gone — the extractor
// output now mirrors the New Job form 1:1, and anything unusual is
// folded into `notes` by the extractor prompt.
//
// Left pane = paste an email body (or upload .pdf/.eml/.txt).
// Right pane = editable extraction preview + a yard-context banner
// (green = on-tow-out from our yard; red = active police impound —
// accept is hard-blocked).

const STANDARD_FIELDS = [
  { key: "customer_name", label: "Customer / Requestor", required: true },
  { key: "customer_phone", label: "Phone", required: false },
  { key: "address", label: "Pickup Address", required: true },
  { key: "drop_off_address", label: "Drop-off Address", required: false },
  { key: "vehicle", label: "Vehicle", required: true },
  { key: "rego", label: "Rego", required: false },
  { key: "claim_number", label: "Claim #", required: false },
  { key: "service_type", label: "Service", required: true },
  { key: "priority", label: "Priority", required: false },
  { key: "work_order_number", label: "Work Order / Ref #", required: false },
];

function confidenceTone(v) {
  if (v == null) return "";
  if (v >= 0.9) return "border-emerald-300";
  if (v >= 0.7) return "border-amber-300";
  return "border-rose-400 bg-rose-50 dark:bg-rose-950/30";
}

export default function EmailIntakeDialog({ open, onOpenChange, onAccepted }) {
  const [busy, setBusy] = useState(false);
  const [source, setSource] = useState("");           // pasted text
  const [fromEmail, setFromEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [file, setFile] = useState(null);
  const [intake, setIntake] = useState(null);        // parsed result
  const [std, setStd] = useState({});                // editable copy
  const [scheduleMode, setScheduleMode] = useState("now");
  // Job-level backstop dedup (Feb 2026 — extends the same guard used
  // by the New Job form). If the backend returns 409/duplicate_job on
  // accept — a matching non-cancelled job already exists — stash the
  // existing job here and open the confirm modal.
  const [duplicateExisting, setDuplicateExisting] = useState(null);

  const reset = () => {
    setSource(""); setFromEmail(""); setSubject(""); setFile(null);
    setIntake(null); setStd({}); setScheduleMode("now");
    setDuplicateExisting(null);
  };

  const handleParse = async () => {
    if (!source.trim() && !file) {
      toast.error("Paste an email body or pick a file first.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      if (source.trim()) fd.append("text", source.trim());
      if (fromEmail.trim()) fd.append("from_email", fromEmail.trim());
      if (subject.trim()) fd.append("subject", subject.trim());
      if (file) fd.append("file", file);
      const { data } = await api.post("/intake/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setIntake(data);
      setStd({ ...(data.extracted?.standard || {}) });
      // Auto-flip to "later" when the LLM detected a future pickup date.
      if (data.extracted?.standard?.booked_for) setScheduleMode("later");
      toast.success("Parsed — review the extracted fields.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Parse failed");
    } finally {
      setBusy(false);
    }
  };

  const handleAccept = async ({ force = false } = {}) => {
    setBusy(true);
    try {
      const payload = {
        standard: std,
        schedule_mode: scheduleMode,
        booked_for: scheduleMode === "later" ? std.booked_for : null,
      };
      const url = force ? `/intake/${intake.id}/accept?force=true` : `/intake/${intake.id}/accept`;
      const { data: job } = await api.post(url, payload);
      toast.success(`Job created for ${job.rego || job.customer_name}`);
      onAccepted?.(job);
      reset();
      onOpenChange(false);
    } catch (e) {
      // Job-level backstop dedup (Feb 2026): the backend returns 409
      // + `code: duplicate_job` + existing job basics when this intake
      // would duplicate an already-open job. Show the confirm modal
      // so the operator can open the existing job or override.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof detail === "object" && detail?.code === "duplicate_job") {
        setDuplicateExisting(detail.existing);
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Accept failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDiscard = async () => {
    if (!intake) { reset(); return; }
    setBusy(true);
    try {
      await api.post(`/intake/${intake.id}/discard`);
      toast.message("Discarded — email intake row marked closed.");
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Discard failed");
    } finally {
      setBusy(false);
    }
  };

  const yctx = intake?.yard_context || {};
  const conf = intake?.extracted?.confidence || {};
  const impoundBlocked = yctx.is_impound;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] flex flex-col"
        data-testid="email-intake-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Mail className="h-5 w-5 text-blue-600" />
            Import Job from Email
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 overflow-y-auto flex-1 px-1">
          {/* LEFT — source paste / upload */}
          <div className="space-y-3 border-r-0 md:border-r border-slate-200 dark:border-slate-800 md:pr-4">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Source</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="int-from" className="text-xs">From</Label>
                <Input
                  id="int-from"
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                  placeholder="operations@nzra.co.nz"
                  data-testid="intake-from-email"
                  disabled={busy}
                />
              </div>
              <div>
                <Label htmlFor="int-subj" className="text-xs">Subject</Label>
                <Input
                  id="int-subj"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Job details NZRA396166 / EBF172"
                  data-testid="intake-subject"
                  disabled={busy}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="int-body" className="text-xs">Email body</Label>
              <Textarea
                id="int-body"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="Paste the full email body here — plain text is fine."
                className="font-mono text-xs h-56"
                data-testid="intake-body"
                disabled={busy}
              />
            </div>
            <div>
              <Label className="text-xs">Or upload a PDF / .eml / .txt</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="file"
                  accept=".pdf,.eml,.txt,application/pdf,message/rfc822,text/plain"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="flex-1 text-xs file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-slate-100 dark:file:bg-slate-800"
                  data-testid="intake-file"
                  disabled={busy}
                />
                {file && (
                  <button
                    onClick={() => setFile(null)}
                    className="p-1 text-slate-500 hover:text-rose-600"
                    title="Remove file"
                  ><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleParse}
              disabled={busy || (!source.trim() && !file)}
              className="w-full font-semibold"
              data-testid="intake-parse-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Parse
            </Button>

            {intake?.extracted?.raw_extraction_notes && (
              <div className="text-xs text-slate-500 dark:text-slate-400 italic mt-2 p-2 rounded bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800">
                <span className="font-semibold not-italic">LLM notes:</span> {intake.extracted.raw_extraction_notes}
              </div>
            )}
          </div>

          {/* RIGHT — extracted preview */}
          <div className="space-y-3">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Extracted (editable)</div>

            {!intake && (
              <div className="text-sm text-slate-500 dark:text-slate-400 border border-dashed border-slate-300 dark:border-slate-700 rounded-md p-6 text-center">
                Paste an email or upload a file, then hit <span className="font-semibold text-slate-700 dark:text-slate-200">Parse</span>.
              </div>
            )}

            {intake && impoundBlocked && (
              <div className="border-2 border-rose-500 bg-rose-50 dark:bg-rose-950/40 rounded-md p-3" data-testid="intake-impound-block">
                <div className="flex items-center gap-2 text-rose-800 dark:text-rose-200 font-bold text-sm">
                  <ShieldAlert className="h-5 w-5" /> Active police impound
                </div>
                <div className="text-xs text-rose-700 dark:text-rose-300 mt-1">
                  This vehicle ({std.rego}) is under an active police impound. Release paperwork must be completed manually — auto-creating a job here is blocked.
                </div>
              </div>
            )}

            {intake && yctx.in_yard && (
              <div className="border-2 border-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 rounded-md p-3" data-testid="intake-yard-context">
                <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-200 font-bold text-sm">
                  <Warehouse className="h-5 w-5" /> On-tow out from your yard
                </div>
                <div className="text-xs text-emerald-700 dark:text-emerald-300 mt-1">
                  Vehicle currently in <span className="font-semibold">{yctx.yard_name}</span> · {yctx.days_in_storage} days storage ·
                  estimate ${Number(yctx.storage_owed_estimate || 0).toFixed(2)} (@ ${Number(yctx.storage_daily_rate || 0).toFixed(2)}/day)
                </div>
                <div className="text-[11px] text-emerald-700 dark:text-emerald-300 mt-1">
                  On completion we&apos;ll stop the storage clock automatically and add the storage total as a line item on this outbound tow.
                </div>
              </div>
            )}

            {intake && STANDARD_FIELDS.map((f) => (
              <div key={f.key}>
                <Label htmlFor={`std-${f.key}`} className="text-xs flex items-center gap-1">
                  {f.label}
                  {f.required && <span className="text-rose-500">*</span>}
                  {conf[f.key] != null && (
                    <span className="text-[10px] text-slate-400 ml-1">({Math.round(conf[f.key] * 100)}%)</span>
                  )}
                </Label>
                <Input
                  id={`std-${f.key}`}
                  value={std[f.key] ?? ""}
                  onChange={(e) => setStd({ ...std, [f.key]: e.target.value })}
                  className={`h-9 ${confidenceTone(conf[f.key])}`}
                  data-testid={`intake-std-${f.key}`}
                  disabled={busy}
                />
              </div>
            ))}

            {intake && (
              <div>
                <Label htmlFor="std-notes" className="text-xs">Notes</Label>
                <Textarea
                  id="std-notes"
                  value={std.notes ?? ""}
                  onChange={(e) => setStd({ ...std, notes: e.target.value })}
                  className="text-xs h-24"
                  data-testid="intake-std-notes"
                  disabled={busy}
                />
              </div>
            )}

            {intake && (
              <div className="border-t border-slate-200 dark:border-slate-800 pt-3">
                <Label className="text-xs mb-2 block">Schedule</Label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setScheduleMode("now")}
                    className={`flex-1 h-9 text-sm rounded-md border-2 font-semibold transition-colors ${scheduleMode === "now" ? "border-blue-500 bg-blue-50 dark:bg-blue-950/50 text-blue-800 dark:text-blue-200" : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400"}`}
                    data-testid="intake-schedule-now"
                    disabled={busy}
                  >Now</button>
                  <button
                    onClick={() => setScheduleMode("later")}
                    className={`flex-1 h-9 text-sm rounded-md border-2 font-semibold transition-colors ${scheduleMode === "later" ? "border-blue-500 bg-blue-50 dark:bg-blue-950/50 text-blue-800 dark:text-blue-200" : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400"}`}
                    data-testid="intake-schedule-later"
                    disabled={busy}
                  >Book for later</button>
                </div>
                {scheduleMode === "later" && (
                  <div className="mt-2">
                    <DateTime24
                      value={(std.booked_for || "").slice(0, 16)}
                      onChange={(v) => setStd({ ...std, booked_for: v })}
                      disabled={busy}
                      dataTestId="intake-booked-for"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="border-t border-slate-200 dark:border-slate-800 pt-3">
          {intake ? (
            <div className="flex items-center gap-2 w-full">
              <Button
                onClick={handleDiscard}
                variant="outline"
                disabled={busy}
                className="border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-950/30 hover:text-rose-800 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-950/40"
                data-testid="intake-discard-btn"
              >
                <XCircle className="h-4 w-4" /> Discard
              </Button>
              <div className="flex-1" />
              {impoundBlocked && (
                <Badge variant="destructive" className="mr-2"><AlertTriangle className="h-3 w-3 mr-1" /> Blocked</Badge>
              )}
              <Button
                onClick={() => handleAccept()}
                disabled={busy || impoundBlocked}
                className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
                data-testid="intake-accept-btn"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Accept as Job
              </Button>
            </div>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
      <IntakeDuplicateDialog
        existing={duplicateExisting}
        onClose={() => setDuplicateExisting(null)}
        onCreateAnyway={() => {
          setDuplicateExisting(null);
          handleAccept({ force: true });
        }}
      />
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Intake duplicate confirm dialog (Feb 2026)
// -----------------------------------------------------------------------------
// Mirrors the manual New Job form's `DuplicateJobDialog`. Fires when the
// backend's job-level backstop dedup returns 409 on `POST /intake/:id/accept`
// — meaning an existing non-cancelled job already matches this intake's
// rego + vehicle + pickup + drop-off. Gives the operator two escape hatches:
// jump to the existing job's history record, or force-create anyway.
function IntakeDuplicateDialog({ existing, onClose, onCreateAnyway }) {
  const nav = useNavigate();
  if (!existing) return null;
  const openExisting = () => {
    onClose();
    nav(`/history?q=${encodeURIComponent(existing.customer_name || "")}&open=${existing.id}`);
  };
  return (
    <AlertDialog open={!!existing} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="intake-duplicate-job-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>This intake looks like a duplicate</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>An existing job already matches every field (rego, vehicle, pickup, drop-off) from this email — likely the original job that this email is a reply to or a forward of.</p>
              <div className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-md px-3 py-2 text-sm">
                <div className="font-semibold text-slate-800 dark:text-slate-100">
                  {existing.customer_name || "—"}
                </div>
                <div className="text-slate-500 text-xs">
                  Job {existing.id?.slice(0, 8)} · {existing.status}
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel data-testid="intake-duplicate-cancel-btn">Cancel</AlertDialogCancel>
          <Button
            variant="outline"
            onClick={openExisting}
            data-testid="intake-duplicate-open-btn"
          >
            Open Existing
          </Button>
          <AlertDialogAction
            onClick={onCreateAnyway}
            data-testid="intake-duplicate-create-new-btn"
          >
            Create New
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
